import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { 
  LayoutDashboard, 
  Globe, 
  Activity, 
  Server, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  ChevronRight,
  Shield,
  AlertCircle,
  FileText,
  Wrench
} from 'lucide-react'
import { authService } from '../services/auth'

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const location = useLocation()
  const navigate = useNavigate()
  const user = authService.getUser()

  // Check authentication only once on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      // Check localStorage directly to bypass authService caching
      const token = localStorage.getItem('token')
      const tokenKey = localStorage.getItem('auth_token')
      console.log('Layout auth check - token:', token?.substring(0, 20), 'tokenKey:', tokenKey?.substring(0, 20))
      const hasToken = token || tokenKey
      if (!hasToken && location.pathname !== '/login') {
        console.log('No token found, redirecting to login')
        navigate('/login')
      } else if (hasToken) {
        console.log('Token found, staying on page')
      }
      setIsCheckingAuth(false)
    }, 500) // 500ms delay to ensure localStorage is ready after page reload
    
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount, not on route change

  const handleLogout = () => {
    authService.logout()
    navigate('/login')
  }

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/zones', label: 'DNS Zones', icon: Globe },
    { path: '/monitoring', label: 'Monitoring', icon: Activity },
    { path: '/dns', label: 'DNS Server', icon: Server },
    { path: '/config-builder', label: 'Config Builder', icon: Wrench },
  ]

  // Admin only items
  if (authService.isAdmin()) {
    navItems.push(
      { path: '/users', label: 'Users', icon: Users },
      { path: '/settings', label: 'Settings', icon: Settings }
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-20'} 
          bg-gradient-to-b from-slate-900 to-slate-800 
          text-white transition-all duration-300 flex flex-col`}
      >
        {/* Logo Area */}
        <div className="h-16 flex items-center px-4 border-b border-slate-700">
          <img 
            src="https://netwallexpert.com/images/logo_netwallexpert.png" 
            alt="Netwall" 
            className="h-10 w-auto"
          />
          {sidebarOpen && (
            <span className="ml-3 font-semibold text-sm">DNS Manager</span>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path || 
                            (item.path !== '/' && location.pathname.startsWith(item.path))
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-sky-600 text-white' 
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="ml-3 text-sm font-medium">{item.label}</span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* User Info */}
        <div className="border-t border-slate-700 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-sky-600 flex items-center justify-center text-sm font-medium">
              {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
            </div>
            {sidebarOpen && (
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.full_name || user?.username}
                </p>
                <p className="text-xs text-slate-400 truncate capitalize">
                  {user?.role}
                </p>
              </div>
            )}
          </div>
          
          <button
            onClick={handleLogout}
            className={`mt-3 flex items-center text-slate-300 hover:text-white transition-colors ${
              sidebarOpen ? 'px-3 py-2 w-full rounded-lg hover:bg-slate-700' : 'justify-center'
            }`}
          >
            <LogOut className="h-4 w-4" />
            {sidebarOpen && <span className="ml-2 text-sm">Logout</span>}
          </button>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-20 -right-3 bg-sky-600 text-white rounded-full p-1 shadow-lg hover:bg-sky-700 transition-colors"
        >
          <ChevronRight className={`h-4 w-4 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold text-slate-800">
              Netwall DNS Management System
            </h1>
            <span className="px-2 py-1 bg-sky-100 text-sky-700 text-xs rounded-full font-medium">
              v1.0.0
            </span>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Status Indicator */}
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
              <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
              <span>System Online</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isCheckingAuth ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            </div>
          ) : (
            <Outlet />
          )}
        </div>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 py-3 px-6">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center space-x-4">
              <img 
                src="https://netwallexpert.com/images/logo_netwallexpert.png" 
                alt="Netwall Expert" 
                className="h-6 w-auto opacity-60"
              />
              <span>Netwall Expert Pvt Ltd, New Delhi, India</span>
            </div>
            <div className="flex items-center space-x-4">
              <span>Contact: +91-9899664703</span>
              <span>support@netwallexpert.com</span>
            </div>
          </div>
          <div className="text-center text-xs text-slate-400 mt-2">
            Copyright © Netwall Expert Pvt Ltd. All rights reserved.
          </div>
        </footer>
      </main>
    </div>
  )
}

export default Layout
